import { Marker, Popup } from "@vis.gl/react-maplibre";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useLiveMapIncidentsActions, usePageContext } from "@ryde/redux";
import {
  IncidentStatusEnum,
  IRideRoute,
  liveMapNewIncidentsAF,
  liveMapRideTrackingRoutesSF,
  Log,
  selectedLiveMapMapRidesSF,
  useDebugMode,
  useFormats,
  useHasPermissions,
  useTimezone,
} from "@ryde/shared";

import { LiveMapIncidentMarkerGlobalStyle } from "./live-map.incidents-layer.styled";
import {
  buildDebugFakeLiveMapIncidents,
  buildIncidentMarkerHtml,
  LIVE_MAP_INCIDENT_LEAFLET_POPUP_CLASS,
  LIVE_MAP_INCIDENT_POPUP_EXTRA_OFFSET_Y,
  LiveMapIncidentPopupView,
  resolveIncidentMapPosition,
  type ILiveMapIncidentMarkerRow,
} from "./parts";

export const LiveMapIncidentsLayerMapLibre: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { debugMode } = useDebugMode();
  const showDebugPreviewIncidentMarkers = debugMode;
  const { timezone } = useTimezone();
  const { dateTimeFormat } = useFormats();
  const mapRides = useRecoilValue(selectedLiveMapMapRidesSF(entity));
  const trackingRoutes = useRecoilValue(liveMapRideTrackingRoutesSF(entity));
  const polledIncidents = useRecoilValue(liveMapNewIncidentsAF(entity));
  const { closeIncidentOnMap, openIncidentDrawerFromLiveMap } =
    useLiveMapIncidentsActions();

  const { hasPermissions: hasIncidentRead } = useHasPermissions({
    entity: "incident",
    action: "read",
  });
  const { hasPermissions: hasIncidentUpdate } = useHasPermissions({
    entity: "incident",
    action: "update",
  });
  const { hasPermissions: hasIncidentUpdateStatus } = useHasPermissions({
    entity: "incident",
    action: "update_status",
  });

  const [incidentIdsOpenedOnMap, setIncidentIdsOpenedOnMap] = useState<
    ReadonlySet<number>
  >(() => new Set());
  const [openedPopupIncidentId, setOpenedPopupIncidentId] = useState<
    number | null
  >(null);

  const trackingByRideId = useMemo(() => {
    const m = new Map<number, IRideRoute>();

    for (const r of trackingRoutes) {
      if (r.rideId) {
        m.set(r.rideId, r);
      }
    }

    return m;
  }, [trackingRoutes]);

  const rideById = useMemo(
    () => new Map(mapRides.map(r => [r.id, r])),
    [mapRides],
  );

  const debugFakeIncidents = useMemo(
    () =>
      showDebugPreviewIncidentMarkers
        ? buildDebugFakeLiveMapIncidents(
            mapRides,
            t("liveMap.incident.previewTypeName"),
            timezone,
          )
        : [],
    [mapRides, showDebugPreviewIncidentMarkers, t, timezone],
  );

  const dateTimeRowLabel = t("liveMap.incident.dateTime");
  const rideNumberRowLabel = t("liveMap.incident.rideNumber");
  const driverRowLabel = t("liveMap.incident.driver");
  const closeLabel = t("liveMap.incident.closeIncident");
  const openIncidentTooltip = t("liveMap.incident.openIncident");

  const markers = useMemo((): ILiveMapIncidentMarkerRow[] => {
    const rows: ILiveMapIncidentMarkerRow[] = [];

    if (showDebugPreviewIncidentMarkers) {
      for (const fake of debugFakeIncidents) {
        rows.push({
          incident: fake,
          position: {
            latitude: fake.latitude,
            longitude: fake.longitude,
          },
          isPreview: true,
          hasOpenedPopupOnMap: incidentIdsOpenedOnMap.has(fake.id),
        });
      }
    }

    if (!showDebugPreviewIncidentMarkers) {
      for (const inc of polledIncidents) {
        if (inc.status !== IncidentStatusEnum.New) {
          continue;
        }

        const position = resolveIncidentMapPosition(
          inc,
          trackingByRideId,
          rideById,
        );

        if (!position) {
          Log.debug("Live map incident skipped (no coordinates)", {
            id: inc.id,
          });
          continue;
        }

        rows.push({
          incident: inc,
          position,
          isPreview: false,
          hasOpenedPopupOnMap: incidentIdsOpenedOnMap.has(inc.id),
        });
      }
    }

    return rows;
  }, [
    debugFakeIncidents,
    incidentIdsOpenedOnMap,
    showDebugPreviewIncidentMarkers,
    polledIncidents,
    rideById,
    trackingByRideId,
  ]);

  return (
    <>
      <LiveMapIncidentMarkerGlobalStyle />
      {markers.map(({ incident, position, isPreview, hasOpenedPopupOnMap }) => {
        const isActionable = !isPreview && incident.id > 0;
        const markerHtml = buildIncidentMarkerHtml({
          hasOpenedPopupOnMap,
          incidentTypeIconType: incident.incidentTypeIconType,
          incidentTypeColor: incident.incidentTypeColor,
        });
        const isPopupOpen = openedPopupIncidentId === incident.id;

        return (
          <div key={`incident-marker-${incident.id}`}>
            <Marker
              longitude={position.longitude}
              latitude={position.latitude}
              anchor="center"
              pitchAlignment="viewport"
              rotationAlignment="viewport"
              onClick={e => {
                e.originalEvent.stopPropagation();
                setOpenedPopupIncidentId(incident.id);
                setIncidentIdsOpenedOnMap(prev => {
                  if (prev.has(incident.id)) {
                    return prev;
                  }
                  const next = new Set(prev);
                  next.add(incident.id);
                  return next;
                });
              }}
            >
              <div
                className="maplibre-incident-marker"
                dangerouslySetInnerHTML={{ __html: markerHtml }}
              />
            </Marker>

            {isPopupOpen && (
              <Popup
                longitude={position.longitude}
                latitude={position.latitude}
                anchor="bottom"
                offset={LIVE_MAP_INCIDENT_POPUP_EXTRA_OFFSET_Y}
                className={LIVE_MAP_INCIDENT_LEAFLET_POPUP_CLASS}
                onClose={() => setOpenedPopupIncidentId(null)}
                closeOnClick={false}
              >
                <LiveMapIncidentPopupView
                  incident={incident}
                  isPreviewMarker={isPreview}
                  dateTimeRowLabel={dateTimeRowLabel}
                  rideNumberRowLabel={rideNumberRowLabel}
                  driverRowLabel={driverRowLabel}
                  closeLabel={closeLabel}
                  openIncidentTooltip={openIncidentTooltip}
                  timezone={timezone}
                  dateTimeFormat={dateTimeFormat}
                  onCloseIncident={() => closeIncidentOnMap(incident.id)}
                  onOpenIncidentDrawer={() =>
                    openIncidentDrawerFromLiveMap(
                      incident.id,
                      incident.rideId ?? undefined,
                    )
                  }
                  onRequestClosePopup={() => setOpenedPopupIncidentId(null)}
                  showCloseIncidentButton={Boolean(
                    hasIncidentUpdateStatus &&
                    isActionable &&
                    incident.status === IncidentStatusEnum.New,
                  )}
                  showOpenIncidentButton={Boolean(
                    hasIncidentRead && isActionable,
                  )}
                  canEditIncident={Boolean(hasIncidentUpdate && isActionable)}
                />
              </Popup>
            )}
          </div>
        );
      })}
    </>
  );
};
